import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faDatabase, faSearch, faTimes } from '@fortawesome/free-solid-svg-icons';
import { getItemImageSrc, handleImgError } from '../../../utils/itemUtils';
import Tooltip from '../ItemTooltip';
import styles from './ItemDatabase.module.css';

// ── Constants ───────────────────────────────────────────────────────────────

const CATEGORIES: { key: string; label: string; icon: string }[] = [
  { key: '',           label: 'All Items',    icon: '📦' },
  { key: 'Weapon',     label: 'Weapons',      icon: '⚔️' },
  { key: 'Shield',     label: 'Shields',      icon: '🛡️' },
  { key: 'Armor',      label: 'Armor',        icon: '🥋' },
  { key: 'Accessory',  label: 'Accessories',  icon: '💍' },
  { key: 'Material',   label: 'Materials',    icon: '🪨' },
  { key: 'Potion',     label: 'Potions',      icon: '⚗️' },
  { key: 'Knowledge',  label: 'Knowledge',    icon: '📜' },
  { key: 'Rune',       label: 'Runes',        icon: '🔮' },
];

const SUB_CATEGORY_MAP: Record<string, string[]> = {
  Weapon:    ['Sword', 'Axe', 'Mace', 'Staff', 'Dagger', 'Chain', 'Flail'],
  Armor:     ['Head', 'Shoulders', 'Torso', 'Hands', 'Legs', 'Feet'],
  Accessory: ['Cloak', 'Neck', 'Ring', 'Armring', 'Amulet'],
  Knowledge: ['Scroll', 'Tome', 'Book'],
  Rune:      ['green', 'orange', 'blue', 'red', 'aqua', 'yellow', 'gray', 'purple'],
};

const RACES = ['All', 'Human', 'Elf', 'Dwarf', 'Orc', 'Troll', 'Goblin'];

const SORT_OPTIONS = [
  { value: 'name',  label: 'Name A–Z' },
  { value: 'level', label: 'Level' },
];

const LIMIT = 40;

// ── Types ───────────────────────────────────────────────────────────────────

interface ItemRow {
  id: number;
  name: string;
  description: string;
  img?: string;
  itemCategory: string;
  weaponType?: string;
  armorType?: string;
  accessoryType?: string;
  materialType?: string;
  potionType?: string;
  knowledgeType?: string;
  runeColor?: string;
  runeTier?: number;
  requiredLevel?: number;
  value?: number;
  itemRace?: string;
  [key: string]: any;
}

// ── Helper ──────────────────────────────────────────────────────────────────

function getSubLabel(item: ItemRow): string {
  return (
    item.weaponType || item.armorType || item.accessoryType ||
    item.materialType || item.potionType || item.knowledgeType ||
    (item.runeColor ? `${item.runeColor} rune` : '') || ''
  );
}

// ── Component ───────────────────────────────────────────────────────────────

const ItemDatabase: React.FC = () => {
  const [items, setItems]           = useState<ItemRow[]>([]);
  const [total, setTotal]           = useState(0);
  const [page, setPage]             = useState(1);
  const [loading, setLoading]       = useState(false);

  const [category, setCategory]     = useState('');
  const [sub, setSub]               = useState('');
  const [search, setSearch]         = useState('');
  const [race, setRace]             = useState('');
  const [minLevel, setMinLevel]     = useState('');
  const [maxLevel, setMaxLevel]     = useState('');
  const [sort, setSort]             = useState('level');

  const searchTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const totalPages = Math.ceil(total / LIMIT);

  const fetchItems = useCallback(async (opts: {
    category: string; sub: string; search: string; race: string;
    minLevel: string; maxLevel: string; sort: string; page: number;
  }) => {
    setLoading(true);
    try {
      const params: Record<string, string> = {
        limit: String(LIMIT),
        page: String(opts.page),
        sort: opts.sort,
      };
      if (opts.category)  params.category = opts.category;
      if (opts.sub)       params.sub      = opts.sub;
      if (opts.search)    params.search   = opts.search;
      if (opts.race && opts.race !== 'All') params.race = opts.race;
      if (opts.minLevel)  params.minLevel = opts.minLevel;
      if (opts.maxLevel)  params.maxLevel = opts.maxLevel;

      const res = await axios.get('/api/item/database', { params, withCredentials: true });
      setItems(res.data.items);
      setTotal(res.data.total);
    } catch {
      setItems([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, []);

  // Initial load and filter changes
  useEffect(() => {
    setPage(1);
    fetchItems({ category, sub, search, race, minLevel, maxLevel, sort, page: 1 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [category, sub, race, minLevel, maxLevel, sort]);

  // Debounced search
  const handleSearchChange = (val: string) => {
    setSearch(val);
    if (searchTimer.current) clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => {
      setPage(1);
      fetchItems({ category, sub, search: val, race, minLevel, maxLevel, sort, page: 1 });
    }, 350);
  };

  const handlePageChange = (p: number) => {
    setPage(p);
    fetchItems({ category, sub, search, race, minLevel, maxLevel, sort, page: p });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleCategoryClick = (key: string) => {
    setCategory(key);
    setSub('');
  };

  const handleClear = () => {
    setSearch('');
    setRace('');
    setMinLevel('');
    setMaxLevel('');
    setSort('name');
  };


  return (
    <div className={styles.pageWrap}>
      {/* ── Sidebar ── */}
      <aside className={styles.sidebar}>
        <div className={styles.sidebarHeader}>
          <FontAwesomeIcon icon={faDatabase} className={styles.sidebarIcon} />
          <span>Item Database</span>
        </div>

        <nav className={styles.sidebarNav}>
          {CATEGORIES.map(({ key, label, icon }) => {
            const subs = SUB_CATEGORY_MAP[key] || [];
            const isActive = category === key;
            return (
              <React.Fragment key={key}>
                <button
                  className={`${styles.catBtn} ${isActive ? styles.catBtnActive : ''}`}
                  onClick={() => handleCategoryClick(key)}
                >
                  <span className={styles.catIcon}>{icon}</span>
                  {label}
                  {subs.length > 0 && (
                    <span className={styles.catChevron}>{isActive ? '▾' : '▸'}</span>
                  )}
                </button>
                {isActive && subs.length > 0 && (
                  <div className={styles.subCatList}>
                    <button
                      className={`${styles.subBtn} ${sub === '' ? styles.subBtnActive : ''}`}
                      onClick={() => setSub('')}
                    >
                      All {label}
                    </button>
                    {subs.map(s => (
                      <button
                        key={s}
                        className={`${styles.subBtn} ${sub === s ? styles.subBtnActive : ''}`}
                        onClick={() => setSub(s)}
                      >
                        {s.charAt(0).toUpperCase() + s.slice(1)}
                      </button>
                    ))}
                  </div>
                )}
              </React.Fragment>
            );
          })}
        </nav>
      </aside>

      {/* ── Main Content ── */}
      <main className={styles.main}>
        {/* Toolbar */}
        <div className={styles.toolbar}>
          {/* Search */}
          <div className={styles.searchWrap}>
            <FontAwesomeIcon icon={faSearch} className={styles.searchIcon} />
            <input
              className={styles.searchInput}
              type="text"
              placeholder="Search items…"
              value={search}
              onChange={e => handleSearchChange(e.target.value)}
            />
          </div>

          {/* Race filter */}
          <span className={styles.filterLabel}>Race:</span>
          <select
            className={styles.filterSelect}
            value={race}
            onChange={e => setRace(e.target.value)}
          >
            <option value="">Any race</option>
            {RACES.slice(1).map(r => (
              <option key={r} value={r}>{r}</option>
            ))}
          </select>

          {/* Level range */}
          <div className={styles.levelRange}>
            <span className={styles.filterLabel}>Lvl:</span>
            <input
              className={styles.levelInput}
              type="number"
              min={1}
              max={99}
              placeholder="min"
              value={minLevel}
              onChange={e => setMinLevel(e.target.value)}
            />
            <span className={styles.filterLabel}>–</span>
            <input
              className={styles.levelInput}
              type="number"
              min={1}
              max={99}
              placeholder="max"
              value={maxLevel}
              onChange={e => setMaxLevel(e.target.value)}
            />
          </div>

          {/* Sort */}
          <span className={styles.filterLabel}>Sort:</span>
          <select
            className={styles.filterSelect}
            value={sort}
            onChange={e => setSort(e.target.value)}
          >
            {SORT_OPTIONS.map(o => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>

          {/* Clear */}
          {(search || race || minLevel || maxLevel || sort !== 'name') && (
            <button className={styles.clearBtn} onClick={handleClear}>
              <FontAwesomeIcon icon={faTimes} style={{ marginRight: 4 }} />
              Clear
            </button>
          )}
        </div>

        {/* Results header */}
        <div className={styles.resultsHeader}>
          <span className={styles.resultsCount}>
            {loading ? 'Loading…' : (
              <>Showing <strong>{items.length}</strong> of <strong>{total}</strong> items</>
            )}
          </span>
        </div>

        {/* Grid */}
        {!loading && items.length === 0 ? (
          <div className={styles.stateBox}>
            <span className={styles.stateIcon}>🔍</span>
            <span>No items found. Try adjusting your filters.</span>
          </div>
        ) : (
          <div className={`${styles.itemGrid} ${loading ? styles.itemGridLoading : ''}`}>
            {items.map(item => (
              <Tooltip key={item.id} item={item} align="center">
                <div className={styles.itemCard}>
                  <div className={styles.itemImgWrap}>
                    <img
                      className={styles.itemImg}
                      src={getItemImageSrc(item)}
                      alt={item.name}
                      loading="lazy"
                      onError={handleImgError}
                    />
                  </div>
                  <div className={styles.itemInfo}>
                    <div className={styles.itemName}>{item.name}</div>
                    <div className={styles.itemMeta}>
                      {item.requiredLevel != null && item.requiredLevel > 0 && (
                        <span className={`${styles.chip} ${styles.chipLevel}`}>Lvl {item.requiredLevel}</span>
                      )}
                      {getSubLabel(item) && (
                        <span className={styles.chip}>
                          {getSubLabel(item).charAt(0).toUpperCase() + getSubLabel(item).slice(1)}
                        </span>
                      )}
                      {item.itemRace && item.itemRace !== 'All' && (
                        <span className={`${styles.chip} ${styles.chipRace}`}>{item.itemRace}</span>
                      )}
                    </div>
                  </div>
                </div>
              </Tooltip>
            ))}
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className={styles.pagination}>
            <button
              className={styles.pageBtn}
              onClick={() => handlePageChange(1)}
              disabled={page === 1}
            >«</button>
            <button
              className={styles.pageBtn}
              onClick={() => handlePageChange(page - 1)}
              disabled={page === 1}
            >‹ Prev</button>

            {Array.from({ length: Math.min(totalPages, 7) }, (_, i) => {
              // Show pages around current
              let p: number;
              if (totalPages <= 7) {
                p = i + 1;
              } else if (page <= 4) {
                p = i + 1;
                if (i === 6) p = totalPages;
              } else if (page >= totalPages - 3) {
                p = totalPages - 6 + i;
                if (i === 0) p = 1;
              } else {
                const offsets = [-3, -2, -1, 0, 1, 2, 3];
                p = page + offsets[i];
                if (i === 0) p = 1;
                if (i === 6) p = totalPages;
              }
              return (
                <button
                  key={p}
                  className={`${styles.pageBtn} ${page === p ? styles.pageBtnActive : ''}`}
                  onClick={() => handlePageChange(p)}
                >
                  {p}
                </button>
              );
            })}

            <button
              className={styles.pageBtn}
              onClick={() => handlePageChange(page + 1)}
              disabled={page === totalPages}
            >Next ›</button>
            <button
              className={styles.pageBtn}
              onClick={() => handlePageChange(totalPages)}
              disabled={page === totalPages}
            >»</button>
          </div>
        )}
      </main>
    </div>
  );
};

export default ItemDatabase;
